import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

// 🔹 Define the current version of the slice
const SLICE_VERSION = '1.2';
const DEFAULT_LIST_VIEW_STATE = {
  currentPage: 1,
  shipmentsPerPage: 50,
};
const DEFAULT_STATUS_FILTER = "New";

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

/**
 * Tab ids the module rebuilds for itself on load, whose state is never orphaned:
 * the org's own tabs, which come from `organizationTypes` rather than from here.
 */
const REBUILT_TAB_IDS = ["shipment-list", "drop-trailers"];

/**
 * Pinned view tabs are published only once their records come back from the API,
 * so at rehydrate they are not in any tab list yet. Their keys all take this shape
 * (see `viewTabStateKey`), and are kept for that reason.
 */
const PINNED_TAB_KEY_PREFIX = "view-tab-";

/**
 * Drop the state of tabs that no longer exist, once, as the persisted slice is
 * read back.
 *
 * Tabs closed before their state was cleaned up left their page, filter and rules
 * behind, and nothing ever removed them — every session added more. Only keys that
 * can be *proven* orphaned go: anything belonging to a tab still in the list, to a
 * tab the module rebuilds itself, or to a pinned tab that hasn't loaded yet is
 * kept, because deleting one of those is how a tab loses its filters.
 */
export const pruneClosedTabState = (slice) => {
  const liveIds = [
    ...REBUILT_TAB_IDS,
    ...(Array.isArray(slice?.dynamicTabs) ? slice.dynamicTabs : [])
      .map((tab) => tab?.id)
      .filter(Boolean),
  ];

  const isLive = (key) =>
    key.startsWith(PINNED_TAB_KEY_PREFIX) ||
    liveIds.some((id) => key === id || key.startsWith(`${id}-`));

  const keepLive = (map = {}) =>
    Object.fromEntries(Object.entries(map).filter(([key]) => isLive(key)));

  return {
    ...slice,
    listViewState: keepLive(slice?.listViewState),
    advancedFilters: keepLive(slice?.advancedFilters),
  };
};

/**
 * Drop everything a closed tab was keeping: its page and quick filter, and its
 * rule-based filters. Sub-tables file their own state under `<tabId>-<name>` — the
 * search tab's grid does — so those go with it, otherwise a session's worth of
 * closed tabs accumulates in the store and in the localStorage copy of it.
 */
const clearStateForTab = (state, tabId) => {
  if (!tabId) return;
  const owns = (key) => key === tabId || key.startsWith(`${tabId}-`);

  for (const key of Object.keys(state.listViewState)) {
    if (owns(key)) delete state.listViewState[key];
  }
  for (const key of Object.keys(state.advancedFilters)) {
    if (owns(key)) delete state.advancedFilters[key];
  }
};

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

// 🔹 Exported initial state for use in version-based merging
/**
 * The one bit of overview state that outlives the session.
 *
 * The strip opens by default — the widgets are what it is for — and stays open until
 * the user folds it away. That choice can't live only in the persisted store, which
 * `resetReduxState()` clears at every login and session expiry: a user who folded the
 * strip away would find it open again on their next login, and every login after.
 */
const OVERVIEW_COLLAPSED_KEY = "shipmentOverviewCollapsed";

/** Collapsed only if the user has said so; anything unreadable means open. */
export const readOverviewCollapsed = () => {
  try {
    return localStorage.getItem(OVERVIEW_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

/** Called from the toggle itself, so only a deliberate fold is remembered. */
export const rememberOverviewCollapsed = (collapsed) => {
  try {
    localStorage.setItem(OVERVIEW_COLLAPSED_KEY, String(Boolean(collapsed)));
  } catch {
    /* storage unavailable — the session's own state still holds */
  }
};

export const initialState = {
  _version: SLICE_VERSION,
  defaultTabs: [
    { id: 'shipment-list', name: 'Shipment List' },
    // You can uncomment or add more default tabs as needed
    // { id: 'shipment-form', name: 'Shipment Form' },
  ],

  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],
  statusFilter: DEFAULT_STATUS_FILTER,
  // Whether the Shipment Overview strip is folded away. Kept here rather than in the
  // module so leaving the module and coming back doesn't refold it, and seeded from
  // the user's own choice so it opens folded only for someone who folded it.
  overviewCollapsed: readOverviewCollapsed(),
  listViewState: {
    "shipment-list": { ...DEFAULT_LIST_VIEW_STATE },
  },
  advancedFilters: {}, 
};

// 🔹 Create the shipment slice
const shipmentSlice = createSlice({
  name: 'shipments',
  initialState,
  reducers: {
    addTab: (state, action) => {
      let tabDetails;

      if (action.payload) {
        tabDetails = action.payload;
      } else {
        const dynamicId =
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2, 15)}-${state.dynamicTabs.length}`;
        tabDetails = {
          id: `dyn-${dynamicId}`,
          name: `New Shipment ${state.dynamicTabs.length + 1}`,
          isDynamic: true,
          createdAt: Date.now(),
        };
      }

      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(state, tabDetails, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.defaultTabs.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);
      clearStateForTab(state, removedId);

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the default Shipment List tab.
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { index } = action.payload;
      if (index >= 0 && index < state.defaultTabs.length + state.dynamicTabs.length && index !== state.activeTab) {
        pushTabHistory(state, getAllTabs(state)[state.activeTab]?.id);
        state.activeTab = index;
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];
    },
    setListViewState: (state, action) => {
      const { stateKey = "shipment-list", ...rest } = action.payload || {};
      const existing = state.listViewState[stateKey] || { ...DEFAULT_LIST_VIEW_STATE };
      state.listViewState[stateKey] = {
        ...existing,
        ...rest,
      };
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setOverviewCollapsed: (state, action) => {
      state.overviewCollapsed = Boolean(action.payload);
    },
    setDefaultTabs: (state, action) => {
      const next = (action.payload || []).filter(Boolean);
      if (!next.length) return;

      // activeTab is an index, so adding or dropping a default tab silently moves
      // the user to a different tab. Saved view tabs arrive here asynchronously,
      // well after the user may have opened something — follow the id instead.
      const activeId = getAllTabs(state)[state.activeTab]?.id;
      state.defaultTabs = next;
      const tabsAfter = getAllTabs(state);

      const sameTabIndex = activeId
        ? tabsAfter.findIndex((t) => t.id === activeId)
        : -1;
      if (sameTabIndex >= 0) {
        state.activeTab = sameTabIndex;
        return;
      }

      const maxIndex = tabsAfter.length - 1;
      if (state.activeTab > maxIndex) {
        state.activeTab = Math.max(0, maxIndex);
      }
    },
    /**
     * For a tab that goes without `removeTab` — a pinned view tab, which the tab
     * bar owns and deletes through its own control.
     */
    clearTabState: (state, action) => {
      clearStateForTab(state, action.payload?.stateKey);
    },

    setAdvancedFilters: (state, action) => {
      const { stateKey, filters } = action.payload;
      state.advancedFilters[stateKey] = filters;
    },
    clearAdvancedFilters: (state, action) => {
      const { stateKey } = action.payload;
      delete state.advancedFilters[stateKey];
    },
  
  },

});

// 🔹 Export actions and reducer
export const {
  addTab,
  removeTab,
  setActiveTab,
  resetTabs,
  setListViewState,
  setStatusFilter,
  setOverviewCollapsed,
  clearTabState,
  setDefaultTabs,
  setAdvancedFilters,
  clearAdvancedFilters,
} = shipmentSlice.actions;
export default shipmentSlice.reducer;
